import { Candle } from '../../../proto/generated/candle-data-depot-common'
import { Security } from '../../../proto/generated/security'
import { TechnicalsTimeframeConfig } from '../../../proto/generated/overview-widget'
import { getCandles } from '../../../services/apis/candles'
import { filterDuplicateCandles } from '../../../util/chartUtils'
import { CANDLE_PAGE_SIZE, DEFAULT_TARGET_BARS, TechnicalsCandleSeries } from './types'

const toSeries = (candles: Candle[], priceMultiplier: number): TechnicalsCandleSeries => {
    const mult = priceMultiplier > 0 ? priceMultiplier : 100
    const open: number[] = []
    const high: number[] = []
    const low: number[] = []
    const close: number[] = []
    const volume: number[] = []
    const hl2: number[] = []
    const hlc3: number[] = []
    const timestamps: number[] = []

    candles.forEach((c) => {
        const o = c.open / mult
        const h = c.high / mult
        const l = c.low / mult
        const cl = c.close / mult
        open.push(o)
        high.push(h)
        low.push(l)
        close.push(cl)
        volume.push(c.volume)
        hl2.push((h + l) / 2)
        hlc3.push((h + l + cl) / 3)
        timestamps.push(c.startTimestamp)
    })

    return {
        open,
        high,
        low,
        close,
        volume,
        hl2,
        hlc3,
        timestamps,
        lastClose: close.length ? close[close.length - 1] : NaN
    }
}

export const fetchTechnicalsCandles = async (
    security: Security,
    timeframe: TechnicalsTimeframeConfig,
    targetBars: number = DEFAULT_TARGET_BARS,
    signal?: AbortSignal
): Promise<TechnicalsCandleSeries> => {
    const priceMultiplier = security.priceMultiplier > 0 ? security.priceMultiplier : 100
    let remaining = targetBars
    // Match Seasonals / SecurityRange: request history ending at now.
    let referenceTime = Date.now()
    let merged: Candle[] = []

    while (remaining > 0) {
        if (signal?.aborted) {
            throw new DOMException('Aborted', 'AbortError')
        }

        const chunk = Math.min(remaining, CANDLE_PAGE_SIZE)
        const response = await getCandles({
            body: {
                security: { exchange: security.exchange, code: security.code },
                timeBucket: {
                    timeSeries: timeframe.timeSeries,
                    value: timeframe.timeValue
                },
                referenceTime,
                forwardCandleCount: 0,
                backwardCandleCount: chunk,
                dontGiveExtraCandles: true,
                dontApplyCorporateActionAdjustments: false
            }
        })

        const batch = filterDuplicateCandles(response.backwardCandles) as Candle[]
        if (!batch.length) break

        merged = [...batch, ...merged]
        remaining -= batch.length

        if (batch.length < chunk) break
        referenceTime = batch[0].startTimestamp - 1
    }

    return toSeries(merged, priceMultiplier)
}
